/**
 * Scope of service, as the client sees it.
 *
 * A scope of service is the document a hospital keeps for each clinical unit
 * saying what that unit does: the patients it serves, the services it
 * provides, its hours, staffing and equipment, and what it does not do.
 * Accreditation (DNV PE, Joint Commission LD, CMS CoPs) requires one per
 * unit, current and approved, so this is survey evidence rather than a
 * description.
 *
 * Shapes mirror what the callables in functions/src/scopeOfService return;
 * nothing here is read from Firestore directly.
 */

import { hasAtLeast, type Role } from '../../types/roles';

export const SCOPE_STATUSES = ['draft', 'in_review', 'approved', 'retired'] as const;
export type ScopeStatus = (typeof SCOPE_STATUSES)[number];

export const STATUS_LABEL: Record<ScopeStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  retired: 'Retired',
};

/** The tag colour, from the shared palette in Page.module.css. */
export const STATUS_TONE: Record<ScopeStatus, 'neutral' | 'warn' | 'ok'> = {
  draft: 'neutral',
  in_review: 'warn',
  approved: 'ok',
  retired: 'neutral',
};

/** A stray value becomes draft, the same fallback the previous client used. */
export function toScopeStatus(value: unknown): ScopeStatus {
  return (SCOPE_STATUSES as readonly unknown[]).includes(value) ? (value as ScopeStatus) : 'draft';
}

/** The age bands a unit serves; the backend's fixed list, with labels. */
export const AGE_GROUPS = ['neonate', 'infant', 'pediatric', 'adolescent', 'adult', 'geriatric'] as const;
export type AgeGroup = (typeof AGE_GROUPS)[number];
export const AGE_GROUP_LABEL: Record<AgeGroup, string> = {
  neonate: 'Neonates (0–28 days)',
  infant: 'Infants (to 1 year)',
  pediatric: 'Children (1–12)',
  adolescent: 'Adolescents (13–17)',
  adult: 'Adults (18–64)',
  geriatric: 'Older adults (65+)',
};
export const toAgeGroups = (v: unknown): AgeGroup[] =>
  Array.isArray(v) ? v.filter((x): x is AgeGroup => (AGE_GROUPS as readonly unknown[]).includes(x)) : [];

export interface ScopeSummary {
  id: string;
  unitName: string;
  unitCode: string;
  departmentId: string | null;
  facilityNames: string[];
  status: ScopeStatus;
  version: number;
  effectiveDate: string | null;
  nextReviewDate: string | null;
  updatedAt: string | null;
}

export interface ScopeDetail extends ScopeSummary {
  description: string | null;
  patientPopulations: string[];
  servicesProvided: string[];
  exclusionsLimitations: string | null;
  hoursOfOperation: string | null;
  staffingRequirements: string | null;
  equipmentRequirements: string | null;
  /** The survey-facing profile: who, where, how many beds, who leads it. */
  ageGroups: AgeGroup[];
  bedCount: number | null;
  physicalLocation: string | null;
  unitLeader: string | null;
  medicalDirector: string | null;
  qualityIndicators: string[];
  createdAt: string | null;
}

export interface ScopeVersion {
  id: string;
  version: number;
  createdAt: string | null;
  changeNotes: string | null;
}

export interface ScopeEvent {
  id: string;
  action: string;
  actorEmail: string | null;
  details: Record<string, unknown> | null;
  createdAt: string | null;
}

/** The activity log, in words. An unknown action shows as itself. */
export const EVENT_LABEL: Record<string, string> = {
  created: 'Created',
  updated: 'Edited',
  submitted_for_review: 'Submitted for review',
  approved: 'Approved',
  rejected: 'Rejected',
  retired: 'Retired',
  new_version: 'New version started',
};

export function eventLabel(action: string): string {
  return EVENT_LABEL[action] ?? action.replace(/_/g, ' ');
}

/** What the create and update callables take, less the ids. */
export interface ScopeFields {
  unitName: string;
  unitCode: string;
  departmentId: string | null;
  description: string;
  patientPopulations: string[];
  servicesProvided: string[];
  exclusionsLimitations: string;
  hoursOfOperation: string;
  staffingRequirements: string;
  equipmentRequirements: string;
  ageGroups: AgeGroup[];
  /** Digits or empty; empty clears it. */
  bedCount: string;
  physicalLocation: string;
  unitLeader: string;
  medicalDirector: string;
  qualityIndicators: string[];
  /** "YYYY-MM-DD" or empty. */
  effectiveDate: string;
  nextReviewDate: string;
}

export const EMPTY_FIELDS: ScopeFields = {
  unitName: '',
  unitCode: '',
  departmentId: null,
  description: '',
  patientPopulations: [],
  servicesProvided: [],
  exclusionsLimitations: '',
  hoursOfOperation: '',
  staffingRequirements: '',
  equipmentRequirements: '',
  ageGroups: [],
  bedCount: '',
  physicalLocation: '',
  unitLeader: '',
  medicalDirector: '',
  qualityIndicators: [],
  effectiveDate: '',
  nextReviewDate: '',
};

/**
 * Which actions a person may take on a scope in this status.
 *
 * The truth is the backend's: each callable checks the status and the role
 * itself. This mirrors those checks so a button is not shown for an action
 * the server would refuse — a "Retire" on a draft is a click that ends in an
 * error, and an error is a worse answer than no button.
 */
export function scopeActions(status: ScopeStatus, roles: readonly Role[]) {
  const author = hasAtLeast(roles, 'author');
  const admin = hasAtLeast(roles, 'admin');
  return {
    edit: author && status === 'draft',
    submit: author && status === 'draft',
    approve: admin && status === 'in_review',
    reject: admin && status === 'in_review',
    newVersion: author && status === 'approved',
    retire: admin && status === 'approved',
  };
}

// Shared with the other modules; kept exported here for the pages and tests.
export { toIsoDateTime, toDateInput, formatDay } from '../../shared/dates';
